import { useState } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  Modal,
  Paragraph,
} from '@zillow/constellation-10';
import { IconPlusCircleFilled, IconVideoCameraOutline } from '@zillow/constellation-icons';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_videoUrl,
  useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_videoUrl,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import stringHelper from '../../../utils/stringHelper';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import type { ChangeEvent } from 'react';

type Props = {
  type?: 'listing' | 'unit';
  feedModelId?: string;
  isDisabled?: boolean;
};

const AddVideoLinkModalTrigger = ({ type = 'listing', feedModelId = '', isDisabled }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const [videoLink, setVideoLink] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: uploadVideoLink, isPending } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_videoUrl,
    successToastMessage: 'Video saved',
  });
  const { mutateAsync: uploadUnitVideoLink } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_videoUrl,
    successToastMessage: 'Video saved',
  });

  const handleSave = async () => {
    if (stringHelper.isValidUrl(videoLink)) {
      try {
        if (type === 'listing') {
          await uploadVideoLink({ getParams: { listingAlias, videoUrl: videoLink } });
        } else if (type === 'unit') {
          await uploadUnitVideoLink({ postData: { feedModelId, listingAlias, videoUrl: videoLink } });
        }
      } catch (err) {
        setErrorMessage(DatarouterError.getDisplayMessage(err));
      }
    } else {
      setErrorMessage('Invalid link format');
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (errorMessage) {
      setErrorMessage(null);
    }
    setVideoLink(e.target.value);
  };

  return (
    <Modal
      size={{ base: 'fullScreen', lg: 'md' }}
      trigger={
        <Button
          data-testid="add-video-url-button"
          size="sm"
          tone="brand"
          emphasis="outlined"
          disabled={isDisabled}
          icon={<IconPlusCircleFilled />}
        >
          Add video
        </Button>
      }
      header={<Heading level={2}>Video</Heading>}
      body={
        <>
          <Box css={{ marginRight: '600' }}>
            <Icon size="lg" css={{ color: 'icon.subtle' }} render={<IconVideoCameraOutline />} />
          </Box>
          <Heading level={4} css={{ marginBlock: '400' }}>
            Add a video link
          </Heading>
          <Paragraph css={{ marginBottom: '400' }}>
            {type === 'listing' &&
              'We recommend adding a professional-quality video that highlights your property’s unit and amenity features.'}
            {type === 'unit' &&
              'We recommend adding a professional-quality video that highlights this unit’s features.'}{' '}
          </Paragraph>
          <FormField
            error={!!errorMessage}
            label={<Label required={true}>Video link</Label>}
            control={<Input data-testid="add-video-url-input" onChange={handleOnChange} />}
            description={errorMessage && <FormHelp>{errorMessage}</FormHelp>}
          />
        </>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            data-testid="add-video-save-button"
            tone="brand"
            emphasis="filled"
            onClick={handleSave}
            disabled={isPending}
          >
            Save
          </Button>
        </Box>
      }
    />
  );
};

export default AddVideoLinkModalTrigger;
